import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
} from 'react'

import { ValkTalkPostForm } from '../../../containers/@forms/ValkTalkPostForm/ValkTalkPostForm'
import { ValkTalkPost, ValkTalkPostFormData } from '../../../entities/ValkTalkPost/ValkTalkPost'
import { getPermittedFeedTypes } from '../../../entities/ValkTalkPost/ValkTalkPostHelpers'
import { getPersonFullName } from '../../../helpers'
import usePermissions from '../../../helpers/hooks/usePermissions'
import trans from '../../../helpers/trans'
import { useTypedDispatch, useTypedSelector } from '../../../redux/store'
import { setToast } from '../../../redux/toasts/toastsReducer'
import { saveValkTalkPost } from '../../../redux/valkTalkPost/valkTalkPostActions'
import { setError, setIsSuccessful } from '../../../redux/valkTalkPost/valkTalkPostReducer'
import { ToastType } from '../../../types'

interface ConnectedValkTalkPostFormProps {
    post?: ValkTalkPost
    onSaved: () => void
    onClose: () => void
}

export const ConnectedValkTalkPostForm: FC<ConnectedValkTalkPostFormProps> = ({
    post,
    onSaved,
    onClose,
}): ReactElement => {
    const dispatch = useTypedDispatch()
    const permissions = usePermissions()
    const { user } = useTypedSelector(state => state.authenticatedUserReducer)
    const { isUpdating, isSuccessful, error } = useTypedSelector(state => state.valkTalkPostReducer)

    const permittedFeedTypes = useMemo(() => getPermittedFeedTypes(permissions), [permissions])
    const authorName = user ? getPersonFullName(user.person) : ''

    useEffect(() => () => {
        dispatch(setError(''))
    }, [])

    useEffect(() => {
        if (! isSuccessful) {
            return
        }

        dispatch(setIsSuccessful(false))
        dispatch(setToast({
            text: trans(post
                ? 'containers.forms.valkTalkPostForm.editPostSuccess'
                : 'containers.forms.valkTalkPostForm.addPostSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

        onSaved()
    }, [isSuccessful])

    useEffect(() => {
        if (error) {
            dispatch(setToast({ text: error, type: ToastType.fail }))
            dispatch(setError(''))
        }
    }, [error])

    const handleSubmit = (formData: ValkTalkPostFormData): void => {
        dispatch(saveValkTalkPost(formData, post?.id))
    }

    return (
        <ValkTalkPostForm
            isLoading={isUpdating}
            authorName={authorName}
            permittedFeedTypes={permittedFeedTypes}
            post={post}
            onSubmit={handleSubmit}
            onCancel={onClose}
        />
    )
}
